 
import { useTranslation } from 'react-i18next';
import { format } from 'domains/dates/format';
import Logo from 'assets/expressable-logo.svg?react';
import Table from 'components/Table';
import { Heading, Paragraph } from 'components/Typography';
import { formatCurrencyCents } from 'utils/currency';
import { DATE_SHORT_HUMAN, DAY_MONTH_YEAR } from 'utils/date';

/**
 * NOTE(juan): This page is not meant to be accessed by users, instead this page is meant to be accessed by our external PDF service
 * That's why you'll see many hard-coded font values and no margins
 */

/**
 * NOTE(juan): In order to get the headless chromium to properly pick up page break CSS rules, the parent div can't be a flex
 * (it must be a block element), this and other limitations are present on this. Be very careful with any changes as its very unstable
 */

 
export enum SuperbillDisplayMode {
  Insurance = 'insurance',
  SelfPay = 'self-pay',
}

export interface ServiceLineForDisplay {
  serviceLineID: string;
  cptCode?: string;
  cptDescription?: string;
  charge: number;
  allowed: number;
  paidByPatient: number;
}

export interface Props {
  mode: SuperbillDisplayMode;
  claimFinalized?: boolean;
  renderingProviderName: string;
  renderingProviderLicenseNumber?: string;
  renderingProviderNpi?: string;
  billingProviderName: string | null;
  billingProviderTaxID: string;
  billingProviderNpi: string;
  billingAddressLines: string[];
  serviceAddressLines: string[];
  serviceAddressLine2?: string;
  patientName: string;
  patientDateOfBirth?: string | null;
  patientAddressLines: string[];
  referringPhysicianName?: string;
  clientDiagnosisDescription: string;
  clientDiagnosisCode: string;
  medicalDiagnoseDescription: string;
  medicalDiagnoseCode: string;
  serviceLines: ServiceLineForDisplay[];
  totalCharges: number;
  totalAllowed: number;
  totalPaidByPatient: number;
  balanceDue: number;
  dateOfService: string;
  therapistName: string;
  insuredName?: string | null;
  insurancePlan?: string | null;
  memberID?: string | null;
  placeOfService: string;
  modifier: string;
  billingFaxNumber: string;
  billingEmail: string;
  signatureDate: string;
}

export const SuperbillPDFUI: React.FC<Props> = props => {
  const {
    mode,
    claimFinalized,
    renderingProviderName,
    renderingProviderLicenseNumber,
    renderingProviderNpi,
    billingProviderName,
    billingProviderTaxID,
    billingProviderNpi,
    billingAddressLines,
    serviceAddressLines,
    patientName,
    patientDateOfBirth,
    patientAddressLines,
    referringPhysicianName,
    clientDiagnosisDescription,
    clientDiagnosisCode,
    medicalDiagnoseDescription,
    medicalDiagnoseCode,
    serviceLines,
    totalCharges,
    totalAllowed,
    totalPaidByPatient,
    balanceDue,
    dateOfService,
    therapistName,
    insuredName,
    insurancePlan,
    memberID,
    placeOfService,
    modifier,
    billingFaxNumber,
    billingEmail,
    signatureDate,
  } = props;

  const { t } = useTranslation();

  return (
    <div className="w-full">
      <header className="flex justify-between items-center mb-24">
        <Heading level="h2" data-testid="superbill.pdfPage.title">
          {t('superbill.pdfPage.title', { date: format(new Date(dateOfService), DAY_MONTH_YEAR) })}
        </Heading>

        <Logo className="fill-current text-black-100% w-auto h-[22px]" title="Expressable" />
      </header>

      <Heading level="h6" className="mb-24">
        {t('superbill.pdfPage.renderingProvider.title')}

        {mode === SuperbillDisplayMode.Insurance && (
          <div className="float-right text-gray-600 bg-cream-100 rounded-lg py-2 px-6">
            {t('superbill.pdfPage.claimSubmitted')}
          </div>
        )}
      </Heading>
      <div className="grid grid-cols-3 gap-x-6 gap-y-16">
        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.renderingProvider.name')}
          </Heading>
          <Paragraph data-testid="renderingProviderName">{renderingProviderName}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.renderingProvider.licenseNumber')}
          </Heading>
          <Paragraph data-testid="renderingProviderLicenseNumber">{renderingProviderLicenseNumber}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.renderingProvider.npi')}
          </Heading>
          <Paragraph data-testid="renderingProviderNpi">{renderingProviderNpi}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.renderingProvider.placeOfService')}
          </Heading>
          <Paragraph data-testid="placeOfService">{placeOfService}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.renderingProvider.modifier')}
          </Heading>
          <Paragraph data-testid="modifier">{modifier}</Paragraph>
        </div>
      </div>

      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t('superbill.pdfPage.billingProvider.title')}
      </Heading>
      <div className="grid grid-cols-3 gap-x-6 gap-y-16">
        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.name')}
          </Heading>
          <Paragraph data-testid="billingProviderName">{billingProviderName}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.taxID')}
          </Heading>
          <Paragraph data-testid="billingProviderTaxID">{billingProviderTaxID}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.npi')}
          </Heading>
          <Paragraph data-testid="billingProviderNpi">{billingProviderNpi}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.billingAddress')}
          </Heading>
          <Paragraph as="div">
            {billingAddressLines.map((billingAddressLine, billingAddressIndex) => (
              <div key={billingAddressIndex + 1} data-testid={`billingAddressLine${billingAddressIndex + 1}`}>
                {billingAddressLine}
              </div>
            ))}
          </Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.serviceAddress')}
          </Heading>
          <Paragraph as="div">
            {serviceAddressLines.map((serviceAddressLine, serviceAddressIndex) => (
              <div key={serviceAddressIndex + 1} data-testid={`serviceAddressLine${serviceAddressIndex + 1}`}>
                {serviceAddressLine}
              </div>
            ))}
          </Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.faxNumber')}
          </Heading>
          <Paragraph data-testid="billingFaxNumber">{billingFaxNumber}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.billingProvider.email')}
          </Heading>
          <Paragraph data-testid="billingEmail">{billingEmail}</Paragraph>
        </div>
      </div>

      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t('superbill.pdfPage.patientProvider.title')}
      </Heading>
      <div className="grid grid-cols-3 gap-x-6 gap-y-16">
        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.patientProvider.name')}
          </Heading>
          <Paragraph data-testid="patientName">{patientName}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.patientProvider.dob')}
          </Heading>
          <Paragraph data-testid="patientDateOfBirth">
            {patientDateOfBirth && format(new Date(patientDateOfBirth), DATE_SHORT_HUMAN)}
          </Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.patientProvider.patientAddress')}
          </Heading>
          <Paragraph as="div">
            {patientAddressLines.map((patientAddressLine, patientAddressIndex) => (
              <div key={patientAddressIndex + 1} data-testid={`patientAddressLine${patientAddressIndex + 1}`}>
                {patientAddressLine}
              </div>
            ))}
          </Paragraph>
        </div>

        {mode === SuperbillDisplayMode.Insurance && (
          <>
            <div>
              <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
                {t('superbill.pdfPage.patientProvider.insuredName')}
              </Heading>
              <Paragraph data-testid="insuredName">{insuredName}</Paragraph>
            </div>

            <div>
              <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
                {t('superbill.pdfPage.patientProvider.insurancePlan')}
              </Heading>
              <Paragraph data-testid="insurancePlan">{insurancePlan}</Paragraph>
            </div>

            <div>
              <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
                {t('superbill.pdfPage.patientProvider.memberID')}
              </Heading>
              <Paragraph data-testid="memberID">{memberID}</Paragraph>
            </div>
          </>
        )}

        {referringPhysicianName && (
          <div>
            <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
              {t('superbill.pdfPage.patientProvider.referringPhysician')}
            </Heading>
            <Paragraph data-testid="referringPhysicianName">{referringPhysicianName}</Paragraph>
          </div>
        )}
      </div>

      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t('superbill.pdfPage.diagnoses.title')}
      </Heading>
      <div className="grid grid-cols-2 gap-x-6 gap-y-16">
        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.diagnoses.primary')}
          </Heading>
          <Paragraph data-testid="clientDiagnosisDescription">{clientDiagnosisDescription}</Paragraph>
        </div>

        <div>
          <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
            {t('superbill.pdfPage.diagnoses.icd10Code')}
          </Heading>
          <Paragraph data-testid="clientDiagnosisCode">{clientDiagnosisCode}</Paragraph>
        </div>

        {medicalDiagnoseDescription && (
          <div>
            <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
              {t('superbill.pdfPage.diagnoses.secondary')}
            </Heading>
            <Paragraph data-testid="medicalDiagnoseDescription">{medicalDiagnoseDescription}</Paragraph>
          </div>
        )}

        {medicalDiagnoseCode && (
          <div>
            <Heading level="h8" className="text-black-600 mb-4 text-[10pt]">
              {t('superbill.pdfPage.diagnoses.icd10Code')}
            </Heading>
            <Paragraph data-testid="medicalDiagnoseCode">{medicalDiagnoseCode}</Paragraph>
          </div>
        )}
      </div>

      <hr className="h-2 bg-black-200 w-full my-24" />

      <Heading level="h6" className="mb-24">
        {t('superbill.pdfPage.services.title')}
      </Heading>

      <div className="border border-gray-300 rounded">
        <Table className="border-none">
          <Table.Header>
            <Table.Row>
              <Table.Cell
                isHeader
                className="border border-gray-300 border-t-0 border-l-0 p-10 text-sm font-semibold rounded-tl bg-cream-50"
              >
                {t('superbill.pdfPage.services.date')}
              </Table.Cell>
              <Table.Cell isHeader className="border border-gray-300 border-t-0 p-10 text-sm font-semibold bg-cream-50">
                {t('superbill.pdfPage.services.cpt')}
              </Table.Cell>
              <Table.Cell isHeader className="border border-gray-300 border-t-0 p-10 text-sm font-semibold bg-cream-50">
                {t('superbill.pdfPage.services.charge')}
              </Table.Cell>
              {mode === SuperbillDisplayMode.Insurance && (
                <Table.Cell
                  isHeader
                  className="border border-gray-300 border-t-0 p-10 text-sm font-semibold bg-cream-50"
                >
                  {t('superbill.pdfPage.services.allowedAmount')}
                </Table.Cell>
              )}
              <Table.Cell
                isHeader
                className="border border-gray-300 border-t-0 border-r-0 p-10 text-sm font-semibold rounded-tr bg-cream-50"
              >
                {t('superbill.pdfPage.services.paidByClient')}
              </Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {serviceLines.map(serviceLine => {
              return (
                <Table.Row key={serviceLine.serviceLineID}>
                  <Table.Cell
                    className="border border-gray-300 border-l-0 p-10 align-top"
                    data-testid="serviceLine.dateOfService"
                  >
                    {format(new Date(dateOfService), DATE_SHORT_HUMAN)}
                  </Table.Cell>
                  <Table.Cell className="border border-gray-300 p-10 align-top">
                    <div data-testid="serviceLine.cptCode">{serviceLine.cptCode}</div>
                    <div className="text-xs mt-8" data-testid="serviceLine.cptDescription">
                      {serviceLine.cptDescription}
                    </div>
                  </Table.Cell>
                  <Table.Cell
                    className="border border-gray-300 p-10 text-right align-top"
                    data-testid="serviceLine.charge"
                  >
                    {formatCurrencyCents(serviceLine.charge ?? 0)}
                  </Table.Cell>
                  {mode === SuperbillDisplayMode.Insurance && (
                    <Table.Cell
                      className="border border-gray-300 p-10 text-right align-top"
                      data-testid="serviceLine.allowed"
                    >
                      {formatAllowed(serviceLine.allowed ?? 0, claimFinalized)}
                    </Table.Cell>
                  )}
                  <Table.Cell
                    className="border border-gray-300 border-r-0 p-10 text-right align-top"
                    data-testid="serviceLine.paidByPatient"
                  >
                    <div>{formatCurrencyCents(serviceLine.paidByPatient ?? 0)}</div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
          <Table.Footer>
            <Table.Row className="border-b-0">
              <Table.Cell colSpan={5}>
                <div className="flex justify-end p-10">
                  <table>
                    <tbody>
                      <tr>
                        <td className="text-right">{t('superbill.pdfPage.services.totalCharges')}</td>
                        <td className="text-right pl-32" data-testid="totalCharges">
                          {formatCurrencyCents(totalCharges)}
                        </td>
                      </tr>
                      {mode === SuperbillDisplayMode.Insurance && (
                        <tr>
                          <td className="text-right">{t('superbill.pdfPage.services.totalAllowedAmount')}</td>
                          <td className="text-right pl-32" data-testid="totalAllowed">
                            {formatAllowed(totalAllowed, claimFinalized)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-right">{t('superbill.pdfPage.services.totalPaidByPatient')}</td>
                        <td className="text-right pl-32" data-testid="totalPaidByPatient">
                          {formatCurrencyCents(totalPaidByPatient)}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right">{t('superbill.pdfPage.services.balanceDue')}</td>
                        <td className="text-right pl-32" data-testid="balanceDue">
                          {formatCurrencyCents(balanceDue)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>

      <Paragraph className="md:text-sm text-gray-700 mt-14">{t('superbill.pdfPage.cptCopyright')}</Paragraph>

      <Paragraph className="mb-16 text-[24pt] font-signature" data-testid="therapistName">
        {therapistName}
      </Paragraph>
      <Paragraph className="mb-16 text-[10pt] text-black-600" data-testid="signature.description">
        {t('pdfs.carePlan.signature.description', {
          name: therapistName,
          date: signatureDate,
        })}
      </Paragraph>
    </div>
  );
};

const formatAllowed = (allowed: number, claimFinalized: boolean | undefined) =>
  allowed > 0 || claimFinalized ? formatCurrencyCents(allowed) : '-';
